<template>
  <div>
    <div class="new-comment-form">
      <form
        v-if="canPostComment"
        class="form text-left"
        role="form"
        autocomplete="off"
        id="formComment"
        novalidate
        @submit.prevent="submitComment"
      >
        <div class="d-flex row">
          <div class="col">
            <div v-if="!isReadonly" :class="cssTheme" class="form-group">
              <input-text-validated
                :label="$t('comments.userName')"
                :readonly="isReadonly"
                rules="required"
                type="text"
                vid="cName"
                id="cName"
                :darkMode="isDarkMode"
                v-model="participantName"
                autocomplete="off"
              />
            </div>

            <div :class="cssTheme" class="form-group comment-text mb-0">
              <InputText
                ref="commentTextField"
                :label="$t('comments.newComment')"
                :placeholder="$t('general.placeholderEmpty')"
                :darkMode="isDarkMode"
                :textareaCustomClass="'commentTextarea'"
                v-model="newCommentText"
              />
            </div>

            <div v-if="commentAck" class="commentAck">
              {{ commentAck }}
            </div>
          </div>
        </div>
      </form>
      <div 
      v-if="canPostComment && !hideWhenNewPoll"
      class="d-flex justify-content-end">
        <BtnLink @click.native="submitComment" :disable="disableAddCommentButton" :label="$t('comments.send')" />
      </div>
    </div>
    <div v-if="canSeeList" class="mt-2">
      <div
        class="container-main"
        v-for="comment in commentsList"
        :key="comment.id"
      >
        <div
          class="container-initial py-1 mt-1 mr-1"
          :class="isDarkThem ? 'dark-font-color' : 'light-font-color'"
        >
          {{ initial(comment.user_name) }}
        </div>
        <div :class="cssTheme" class="container-comment py-1 text-size-14">
          <div
            class="comment-header text-weight-semi-bold"
            v-html="commentHeader(comment)"
          ></div>
          <div
            class="comment-text text-weight-regular"
            v-html="html(comment.text)"
          ></div>
        </div>

        <div :class="cssTheme" class="container-icons">
          <div class="delete-icon text-size-14 pointer">
            <font-awesome-icon
              title="Tooltip directive content"
              class="mx-2"
              @click="deleteComment(comment.id)"
              :icon="['fal', 'trash-alt']"
              v-if="canDelete(comment.user_id)"
            />
          </div>
        </div>
      </div>
    </div>
    <ModalConfirm
      v-show="selectedComment"
      :msg="$t('comments.wrnRemoveComment')"
      @confirm="deletedCommentConfirmed"
      @cancel="deletedCommentCanceled"
    />
  </div>
</template>

<script>
import shared from '@/shared.js';

import { mapGetters, mapState } from 'vuex';
import { HTTP } from '@/shared/http';

import { extend, localize } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import de from 'vee-validate/dist/locale/de.json';

import InputText from '@/components/UI/InputText.vue';
import { gridMixin } from '../mixins/GridMixins';

extend('required', required);

localize('de', de);

export default {
  name: 'Comments',
  components: { InputText },
  mixins: [gridMixin],
  props: {
    commentsList: Array,
    canPostComment: {
      type: Boolean,
      default: true,
    },
    canSeeList: {
      type: Boolean,
      default: true,
    },
    disableAddCommentButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedComment: null,
      newCommentText: '',
      newCommentUserName: '',
      receiveNotifications: false,
      participantId: null,
      participantName: null,
      commentInfo: { participantName: '', newCommentText: '' },
      commentAck: '',
      // userName:null
    };
  },

  methods: {
    urlify(text) {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      return text.replace(urlRegex, (url) => {
        let link = url;
        if (url.length > 30) {
          link = url.slice(0, 30) + '...';
        }
        return `<a href="${url}">${link}</a>`;
      });
    },

    submitComment() {
      if (!this.newCommentText.trim()) {
        return;
      }

      if (!this.participantName.trim()) {
        return;
      }

      this.$refs.errors = {};
      const sendComment = {};

      const text = this.urlify(this.newCommentText);
      console.log('submit comment:', text);
      sendComment.text = text;
      sendComment.userName = this.participantName;
      console.log('submit comment:', sendComment);
      this.newCommentText = '';
      this.$refs.commentTextField.clearInput();
      this.$emit('add', sendComment);
      this.commentAck = this.$t('comments.successSent');
      let timeout = window.setTimeout(() => {
        this.commentAck = '';
        clearTimeout(timeout);
      }, 1000);
    },
    canDelete(commentUserId) {
      return this.isOwner || this.userId === commentUserId || !commentUserId;
    },

    deletedCommentConfirmed() {
      console.log('deletedCommentConfirmed');
      this.$emit('delete', this.selectedComment);
      this.selectedComment = null;
    },

    deletedCommentCanceled() {
      this.selectedComment = null;
    },

    deleteComment(commentId) {
      console.log('deleteCommnet:', commentId);
      this.selectedComment = commentId;
    },

    html(str) {
      const text = str.replace(/(?:\r\n|\r|\n)/g, '<br>');
      return text;
    },

    toggleNotifications() {
      if (this.participantId) {
        if (this.receiveNotifications) {
          this.deactivateNotifications(this.participantId);
        } else {
          this.activateNotifications(this.participantId);
        }
      }
    },
    deactivateNotifications(participantId) {
      const apiUrl = process.env.VUE_APP_API_URI_SEGMENT;
      const url = apiUrl + '/participant/comments/deactivate/' + participantId;

      HTTP.put(url)
        .then((response) => {
          console.log('deactivate response ', response);
        })
        .catch((error) => console.log(error));
    },
    activateNotifications(participantId) {
      const apiUrl = process.env.VUE_APP_API_URI_SEGMENT;
      const url = apiUrl + '/participant/comments/activate/' + participantId;

      HTTP.put(url)
        .then((response) => {
          console.log('activate response ', response);
        })
        .catch((error) => console.log(error));
    },

    getNotificationStatusText() {
      return this.receiveNotifications
        ? this.$t('comments.notificationOn')
        : this.$t('comments.notificationOff');
    },
    getNotificationLinkText() {
      return this.receiveNotifications
        ? this.$t('comments.notificationsDeactivate')
        : this.$t('comments.notificationsActivate');
    },
    getNotificationText() {
      let result = '';
      if (this.userId) {
        const userId = this.userId;
        const userParticipants = this.pollParticipants.filter(
          (item) => item.user_id === userId
        );
        const participant =
          userParticipants.length > 0 ? userParticipants[0] : null;
        if (participant) {
          this.participantId = participant.id;
          result = this.$t('comments.notificationText1');
          if (participant.receive_comment_reminder) {
            this.receiveNotifications = true;
          } else {
            this.receiveNotifications = false;
          }
        } else {
          result = this.$t('comments.noParticipantMessage');
        }
      } else {
        result = this.$t('comments.noUserMessage');
      }
      return result;
    },

    getUserName() {
      console.log('getUserName was called...');
      return this.userName;
    },
    commentHeader(comment) {
      const html =
        comment.user_name +
        ' <span class="text-size-12 text-weight-regular">(' +
        this.$t('comments.preDate') +
        ' ' +
        this.lastUpdate(comment.created_at) +
        ' ' +
        this.$t('comments.postDate') +
        ')</span>';
      return html;
    },
    initial(username) {
      if (!username) {
        return ' ';
      }
      // join all the charcter after the space and took maximum 2 character
      return shared.getInitials(username, 0, 1);
    },

    updateParticipantName(poll) {
      if (this.isCurrentUserInList) {
        this.participantName = poll.participants[0].name;

        // rename participant name in all his comments
        const user_id = poll.participants[0].user_id;
        this.commentsList.forEach((comment) => {
          if (comment.user_id == user_id && comment.user_id != null) {
            comment.user_name = this.participantName;
          }
        });
      } else {
        console.log('Comment - no current user found');
      }
    },
  },
  watch: {
    currentParticipantName(newValue, oldValue) {
      // Our fancy notification (2).
      //console.log(`We have new participant name ${newValue} !`);
      //console.log('this.currentParticipantName -->', newValue, oldValue);
      if (oldValue != null && newValue == null) {
        return;
      }
      this.participantName = newValue;
      console.log('this.participantName -->', this.participantName);
    },
    poll: {
      deep: true,
      handler(poll) {
        this.updateParticipantName(poll);
      },
    },
  },
  computed: {
    ...mapGetters({
      isOwner: 'isOwner',
      isDarkMode: 'isDarkMode',
      userId: 'userId',
      pollParticipants: 'pollParticipants',
      pollId: 'pollId',
      userName: 'userName',
      currentParticipantName: 'currentParticipantName',
      hideWhenNewPoll: 'getPollStatus'
    }),
    ...mapState({
      poll: (state) => state.poll.poll,
    }),

    //  title: {
    //       get() {
    //         return this.poll.title;
    //       },
    //       set(value) {
    //         this.$store.commit('updateTitle', value);
    //       },
    //     },

    isCurrentUserInList() {
      if( !this.poll.participants.legth) {
        return false;
      }
      if (this.poll.participants[0].isCurrentUser) {
        return true;
      }
      return false;
    },

    isReadonly() {
      return this.isCurrentUserInList;
    },
    cssTheme() {
      if (this.isDarkMode) {
        return 'dark-mode';
      }
      return 'light-mode';
    },
  },

  beforeCreate() {},

  created() {
    this.lastUpdate = shared.lastUpdate;
    //this.participantName = this.currentParticipantName;
    this.participantName = null;
    this.updateParticipantName(this.poll);
  },

  beforeMount() {},

  mounted() {},

  beforeUpdate() {},

  updated() {
    //this.newCommentUserName = this.currentParticipantName;
  },

  beforeDestroy() {},

  destroyed() {},
};
</script>

<style scoped lang="scss">
@import '@/styles/colors';
//$bg-color: rgba(255, 255, 255, 0.3);

.comments {
  // margin-left: 5vw;
  width: 100%;
}

.container-comment {
  border-bottom: 0px solid $light-grey;
  width: 100%;
}

// .comments-box {
//   // margin-left: 5vw;
//   padding: 10px 20px 20px 20px;
//   border-radius: 10px;
//   background-color: $bg-color;
//   color: #1d1d1d;
//   backdrop-filter: blur(20px);
//   max-width: 800px;
// }

.container-main {
  display: flex;
  align-items: stretch;
  flex-direction: row;
}

.container-main > div {
  padding-top: 0px;
}

.container-icons {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
}

.comment-header {
  text-align: left;
}

.comment-text {
  text-align: left;
  overflow-wrap: break-word;
}

.dark-mode > .comment-text {
  color: $light-grey !important;
}

.comment-container {
  display: flex;
  flex-direction: column;
  width: 50%;
}

.user-image > img {
  max-width: 50px;
}

.new-comment-form {
  padding: 0px;
  display: flex;
  flex-direction: column;
}

.new-comment-form > input {
  margin-bottom: 1rem;
}

.new-comment-form > textarea {
  margin-bottom: 1rem;
}

.comments-notifications {
  text-align: left;
}
</style>

<style scoped lang="scss">
@import '@/styles/style';

body.dark-mode {
  & .k-input,
  .k-label,
  .container-main {
    color: white;
  }
}

body.light-mode {
  & .k-input,
  .k-label,
  .container-main {
    color: $dark-grey;
  }
}

.dark-mode {
  color: white;
}

.light-mode {
  color: $dark-grey;
}
.comment-text.text-weight-regular {
  // white-space: pre;
}

.container-initial {
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid;
  text-align: center;
  font: 12px Arial, sans-serif;
  text-transform: uppercase;
  padding: 8px;
}

.commentAck {
  font-size: $small;
  text-align: center;
  border-radius: 5px;
  color: $white !important;
  background-color: $primary-dark;
}

::v-deep .commentTextarea {
  overflow: auto !important;
}
</style>
